/*
 * Voicemail through mt-prov-sf's device voicemail API (mt-prov-sf
 * docs/voicemail.md): the messages and greetings of the device's lines.
 * The commands carry the native core's names (docs/api.md of mt-vox); the
 * outcome is the command's result data rather than an event.
 *
 *   voicemail.list            {line?, status?}       -> {messages, has_more}
 *   voicemail.audio           {message}              -> {url} (blob: URL)
 *   voicemail.mark            {message, read?, saved?} -> the message
 *   voicemail.delete          {message}
 *   voicemail.greetings       {line?}                -> {line, active_slot, slots, name, max_seconds}
 *   voicemail.greeting_audio  {slot, line?}          -> {url}
 *   voicemail.greeting_save   {slot, wav, line?}     -> greetings (wav: ArrayBuffer, 16-bit mono 8 kHz)
 *   voicemail.greeting_active {slot|null, line?}     -> greetings
 *   voicemail.greeting_delete {slot, line?}          -> greetings
 */
import type { CoreModule } from "../core/core";
import { CommandError, Command, Json } from "../core/types";
import { ProvError } from "./client";
import type { ProvModule } from "./managed";

/** The PBX's direct-to-voicemail target: @prefix (setting voicemail.transfer_prefix,
 * "**097#") + the extension, '#' sent as %23 (not allowed as is in a SIP URI user).
 * null for anything but digits. */
export function voicemailTarget(prefix: string, extension: string): string | null {
  const ext = extension.trim();
  if (!/^\d{1,32}$/.test(ext) || !/^[0-9*#]{1,32}$/.test(prefix)) return null;
  return prefix.replace(/#/g, "%23") + ext;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLOT = /^([1-9]|name)$/;

export class VoicemailModule implements CoreModule {
  private urls: string[] = [];

  constructor(
    private prov: ProvModule,
    private makeUrl: (b: Blob) => string = (b) => URL.createObjectURL(b),
    private dropUrl: (u: string) => void = (u) => URL.revokeObjectURL(u),
  ) {}

  command(c: Command): Promise<Json | void> | undefined {
    if (!c.cmd.startsWith("voicemail.") || c.cmd === "voicemail.call" || c.cmd === "voicemail.get") return undefined;
    return this.run(c).catch((e) => {
      if (e instanceof ProvError) throw new CommandError(e.status === 404 ? "not_found" : e.code, e.message);
      throw e;
    });
  }

  stop(): void {
    this.urls.forEach(this.dropUrl);
    this.urls = [];
  }

  private session() {
    const s = this.prov.session();
    if (!s) throw new CommandError("not_active", "voicemail needs an activated device");
    return s;
  }

  private q(line: unknown, extra: Record<string, string> = {}): string {
    const p = new URLSearchParams(extra);
    if (line !== undefined && line !== null && line !== "") {
      if (!/^\d+$/.test(String(line))) throw new CommandError("invalid", "line");
      p.set("line", String(line));
    }
    const s = p.toString();
    return s ? `?${s}` : "";
  }

  private async blobUrl(res: Response): Promise<{ url: string }> {
    // one audio at a time: the previous blob is released
    this.urls.forEach(this.dropUrl);
    const url = this.makeUrl(await res.blob());
    this.urls = [url];
    return { url };
  }

  private async run(c: Command): Promise<Json | void> {
    const { client, cred } = this.session();
    const msg = () => {
      const id = String(c.message ?? "");
      if (!UUID.test(id)) throw new CommandError("invalid", "message");
      return id;
    };
    const slot = () => {
      const s = String(c.slot ?? "");
      if (!SLOT.test(s)) throw new CommandError("invalid", "slot");
      return s;
    };
    switch (c.cmd) {
      case "voicemail.summary":
        return client.call<Json>("GET", "/v1/voicemail", cred);
      case "voicemail.list":
        return client.call<Json>("GET", `/v1/voicemail/messages${this.q(c.line, { status: String(c.status ?? "all") })}`, cred);
      case "voicemail.audio":
        return this.blobUrl(await client.raw("GET", `/v1/voicemail/messages/${msg()}/audio`, cred));
      case "voicemail.mark": {
        const body: Record<string, boolean> = {};
        if (typeof c.read === "boolean") body.read = c.read;
        if (typeof c.saved === "boolean") body.saved = c.saved;
        if (!Object.keys(body).length) throw new CommandError("invalid", "read or saved");
        return client.call<Json>("PATCH", `/v1/voicemail/messages/${msg()}`, cred, body);
      }
      case "voicemail.delete":
        await client.raw("DELETE", `/v1/voicemail/messages/${msg()}`, cred);
        return;
      case "voicemail.greetings":
        return client.call<Json>("GET", `/v1/voicemail/greetings${this.q(c.line)}`, cred);
      case "voicemail.greeting_audio":
        return this.blobUrl(await client.raw("GET", `/v1/voicemail/greetings/${slot()}/audio${this.q(c.line)}`, cred));
      case "voicemail.greeting_save": {
        const s = slot();
        if (!(c.wav instanceof ArrayBuffer)) throw new CommandError("invalid", "wav");
        const res = await client.raw("PUT", `/v1/voicemail/greetings/${s}${this.q(c.line)}`, cred, c.wav, "audio/wav");
        return (await res.json()) as Json;
      }
      case "voicemail.greeting_active": {
        const v = c.slot;
        if (v !== null && !(typeof v === "number" && v >= 1 && v <= 9)) throw new CommandError("invalid", "slot");
        return client.call<Json>("PUT", `/v1/voicemail/greetings/active${this.q(c.line)}`, cred, { slot: v });
      }
      case "voicemail.greeting_delete":
        await client.raw("DELETE", `/v1/voicemail/greetings/${slot()}${this.q(c.line)}`, cred);
        return client.call<Json>("GET", `/v1/voicemail/greetings${this.q(c.line)}`, cred);
      default:
        throw new CommandError("unknown", c.cmd);
    }
  }
}
